import React, { useState } from 'react';
import { CompanyProfile } from '../types';
import { Building2, Save, Sparkles, Download } from 'lucide-react';
import { apiRequest } from '../utils/firebase';

interface ConfigProfileProps {
  profile: CompanyProfile;
  onSave: (p: CompanyProfile) => void;
  isAdmin: boolean;
}

export default function ConfigProfile({ profile, onSave, isAdmin }: ConfigProfileProps) {
  const [formData, setFormData] = useState<CompanyProfile>({ ...profile });
  const [success, setSuccess] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    try {
      const endpoints = [
        { name: 'hotels', path: '/hotels' },
        { name: 'clients', path: '/clients' },
        { name: 'parkingSpots', path: '/parking' },
        { name: 'accommodationTypes', path: '/accommodation-types' },
        { name: 'accommodations', path: '/accommodations' },
        { name: 'stockCategories', path: '/stock-categories' },
        { name: 'stockItems', path: '/stock-items' },
        { name: 'minibars', path: '/minibars' },
        { name: 'reservations', path: '/reservations' },
        { name: 'profile', path: '/profile' }
      ];
      
      const backup: Record<string, any[]> = {};
      
      for (const endpoint of endpoints) {
        const data = await apiRequest<any[]>(endpoint.path);
        backup[endpoint.name] = Array.isArray(data) ? data : [data];
      }
      
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(backup, null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", "backup_sistema_hotel.json");
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    } catch (error) {
      console.error("Erro ao exportar dados:", error);
      alert("Erro ao exportar dados. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-5">
        <div>
          <h1 id="header-title-profile" className="text-xl font-semibold tracking-tight text-zinc-950">
            Perfil da Hospedagem
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Configure as credenciais jurídicas, contatos corporativos e chave de recebimentos PIX da sua pousada.
          </p>
        </div>
        <button
          type="button"
          onClick={handleExportData}
          disabled={exporting}
          className="flex items-center gap-1.5 px-4 py-2 border border-zinc-300 text-zinc-800 font-semibold text-xs rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer disabled:opacity-50"
        >
          {exporting ? 'Exportando...' : <><Download className="w-3.5 h-3.5" /> Exportar Dados do Sistema</>}
        </button>
      </div>

      <div className="bg-white border border-zinc-200/80 rounded-xl overflow-hidden shadow-sm">
        <div className="border-b border-zinc-100 bg-zinc-50 px-5 py-4 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-zinc-600" />
          <h2 className="text-xs font-bold text-zinc-800 uppercase tracking-wider">
            Dados Gerais da Empresa
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-5 md:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="input-prof-name" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">
                Nome Fantasia *
              </label>
              <input
                type="text"
                id="input-prof-name"
                disabled={!isAdmin}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/30 disabled:bg-zinc-100 disabled:text-zinc-400"
                required
              />
            </div>

            <div>
              <label htmlFor="input-prof-corp" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">
                Razão Social
              </label>
              <input
                type="text"
                id="input-prof-corp"
                disabled={!isAdmin}
                value={formData.corporateName}
                onChange={(e) => setFormData({ ...formData, corporateName: e.target.value })}
                className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/30 disabled:bg-zinc-100 disabled:text-zinc-400"
              />
            </div>

            <div>
              <label htmlFor="input-prof-doc" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">
                CNPJ / Inscrição Municipal
              </label>
              <input
                type="text"
                id="input-prof-doc"
                disabled={!isAdmin}
                value={formData.document}
                onChange={(e) => setFormData({ ...formData, document: e.target.value })}
                className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/30 disabled:bg-zinc-100 disabled:text-zinc-400"
                placeholder="Ex. 28.190.283/0001-92"
              />
            </div>

            <div>
              <label htmlFor="input-prof-phone" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">
                Telefone de Contato
              </label>
              <input
                type="text"
                id="input-prof-phone"
                disabled={!isAdmin}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/30 disabled:bg-zinc-100 disabled:text-zinc-400"
                placeholder="Ex. (21) 2634-1922"
              />
            </div>

            <div>
              <label htmlFor="input-prof-email" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">
                E-mail Administrativo
              </label>
              <input
                type="email"
                id="input-prof-email"
                disabled={!isAdmin}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/30 disabled:bg-zinc-100 disabled:text-zinc-400"
                placeholder="Ex. financeiro@recantodosol.com"
              />
            </div>

            <div>
              <label htmlFor="input-prof-pix" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">
                Chave PIX Oficial (para Recibos)
              </label>
              <input
                type="text"
                id="input-prof-pix"
                disabled={!isAdmin}
                value={formData.pixKey}
                onChange={(e) => setFormData({ ...formData, pixKey: e.target.value })}
                className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/30 disabled:bg-zinc-100 disabled:text-zinc-400"
                placeholder="Ex. CNPJ, E-mail ou Celular"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="input-prof-addr" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">
                Endereço Físico Completo
              </label>
              <textarea
                id="input-prof-addr"
                disabled={!isAdmin}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={2}
                className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/30 disabled:bg-zinc-100 disabled:text-zinc-400"
                placeholder="Av. Principal, 100 - Búzios, RJ"
              />
              <span className="text-xxs text-zinc-400 block mt-1">Este endereço aparecerá formalmente no cabeçalho das reservas e recibos e checklists de check-out gerados pelo sistema.</span>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 flex items-start gap-2.5">
            <span className="text-xs">⚠️</span>
            <span className="text-xxs block font-medium leading-normal">
              <strong>Atenção:</strong> Qualquer alteração feita nestes campos altera imediatamente e retroativamente os PDFs dos orçamentos, termos e recibos gerados no sistema.
            </span>
          </div>

          {isAdmin && (
            <div className="flex items-center justify-end border-t border-zinc-100 pt-4">
              <button
                type="submit"
                id="btn-save-profile"
                className="flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white font-semibold text-xs rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <Save className="w-3.5 h-3.5" /> Salvar Alterações
              </button>
            </div>
          )}
        </form>
      </div>

      {success && (
        <div id="save-profile-success" className="fixed bottom-4 right-4 p-3.5 bg-zinc-900 text-white rounded-lg shadow-lg flex items-center gap-2 text-xs border border-zinc-800 animate-slide-up">
          <Sparkles className="w-4 h-4 text-yellow-400 animate-pulse" />
          <span>Configurações do perfil atualizadas com sucesso!</span>
        </div>
      )}
    </div>
  );
}
